// NewConversationSheet — the mobile "start a conversation" bottom sheet:
// a directory field plus a known-people picker that opens or creates a DM.

import { useCallback, useId, useMemo, useState, type CSSProperties } from "react";
import { Search } from "lucide-react";

import { Dialog } from "~/components/Dialog";
import { UserAvatar } from "~/components/UserSearch";
import { mobileStrings } from "~/components/mobile/strings";
import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { getMembers, type UserDirectoryEntry } from "~/lib/matrix";
import { useDirectorySearch } from "~/lib/use-directory-search";
import { useUnderline } from "~/lib/underline";
import { ICON_STROKE } from "~/lib/icon";

// `@localpart:domain` with no whitespace and exactly one colon-delimited
// domain segment — the shape Matrix requires for a user id.
const MXID_RE = /^@[^\s:]+:[^\s:]+$/;

export interface NewConversationSheetProps {
  /** Navigate into a room once the DM is opened or created. */
  onOpenRoom: (roomId: string) => void;
  /** Close request — Escape, backdrop, or after a person is selected. */
  onClose: () => void;
}

const PANEL_STYLE: CSSProperties = {
  width: "100%",
  maxWidth: "100%",
  height: "70vh",
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  paddingBottom: "env(safe-area-inset-bottom, 0px)",
};

export function NewConversationSheet({ onOpenRoom, onClose }: NewConversationSheetProps) {
  const { client, userId, createDirectRoom, searchUserDirectory } = useMatrix();
  const rooms = useMatrixRooms();
  const titleId = useId();
  const underline = useUnderline();

  const [term, setTerm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = term.trim();
  const { results, loading } = useDirectorySearch(searchUserDirectory, term, {
    excludeUserIds: userId ? [userId] : undefined,
  });

  // Known people: every joined-room member except yourself and integration
  // bots, de-duped. People you have a DM with come first, most recent
  // conversation first; everyone else follows by name. `getMembers` reads
  // the SDK's member cache — no separate contacts model.
  const knownPeople = useMemo<UserDirectoryEntry[]>(() => {
    if (!client) return [];
    const lastDmActivity = new Map<string, number>();
    for (const room of rooms) {
      if (!room.isDirect) continue;
      for (const directUserId of room.directUserIds) {
        lastDmActivity.set(
          directUserId,
          Math.max(lastDmActivity.get(directUserId) ?? 0, room.lastActivityTs),
        );
      }
    }
    const byId = new Map<string, UserDirectoryEntry>();
    for (const room of rooms) {
      if (room.isSpace || room.archived !== null) continue;
      for (const member of getMembers(client, room.id)) {
        if (member.membership === "invite") continue;
        if (member.userId === userId || byId.has(member.userId)) continue;
        if (isIntegrationBot(member.userId)) continue;
        byId.set(member.userId, {
          userId: member.userId,
          displayName: member.displayName,
          avatarUrl: member.avatarUrl,
        });
      }
    }
    return Array.from(byId.values()).sort(
      (a, b) =>
        (lastDmActivity.get(b.userId) ?? -1) - (lastDmActivity.get(a.userId) ?? -1) ||
        a.displayName.localeCompare(b.displayName),
    );
  }, [client, rooms, userId]);

  const requestClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  // Tapping a person is the whole flow: open (or reuse) the DM.
  const handleSelect = useCallback(
    async (person: UserDirectoryEntry) => {
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        const roomId = await createDirectRoom({ userIds: [person.userId] });
        onOpenRoom(roomId);
        onClose();
      } catch {
        setError(mobileStrings.newConversation.error);
      } finally {
        setBusy(false);
      }
    },
    [busy, createDirectRoom, onClose, onOpenRoom],
  );

  const isValidMxid = MXID_RE.test(trimmed) && trimmed !== userId;
  const mxidEntry: UserDirectoryEntry = {
    userId: trimmed,
    displayName: trimmed,
    avatarUrl: null,
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    if (results.length > 0) {
      event.preventDefault();
      void handleSelect(results[0]!);
    } else if (!loading && isValidMxid) {
      event.preventDefault();
      void handleSelect(mxidEntry);
    }
  };

  const searching = trimmed.length > 0;

  return (
    <Dialog onClose={requestClose} align="bottom" labelledBy={titleId} panelStyle={PANEL_STYLE}>
      <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
        <header
          style={{
            padding: "var(--chat-space-2) var(--chat-space-4) var(--chat-space-2)",
          }}
        >
          <h2
            id={titleId}
            style={{
              margin: 0,
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              fontWeight: 300,
              fontSize: "var(--chat-type-body-lg-size)",
              lineHeight: "var(--chat-type-body-lg-line)",
            }}
          >
            {mobileStrings.newConversation.title}
          </h2>
        </header>
        <div style={{ padding: "var(--chat-space-2) var(--chat-space-4)" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-2)",
              height: "var(--chat-directory-row-height)",
              borderBottom: `1px solid ${underline.borderColor}`,
              transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
            }}
          >
            <Search
              aria-hidden
              size={14}
              strokeWidth={ICON_STROKE}
              style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
            />
            <input
              type="text"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              onKeyDown={handleInputKeyDown}
              onFocus={underline.onFocus}
              onBlur={underline.onBlur}
              placeholder={mobileStrings.newConversation.searchPlaceholder}
              aria-label={mobileStrings.newConversation.searchLabel}
              autoFocus
              style={{
                flex: 1,
                minWidth: 0,
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--chat-fg-primary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-md-size)",
                lineHeight: "var(--chat-type-body-md-line)",
              }}
            />
          </div>
        </div>
        {error !== null && (
          <p
            role="alert"
            style={{
              margin: 0,
              padding: "0 var(--chat-space-4) var(--chat-space-2)",
              color: "var(--chat-state-danger)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
            }}
          >
            {error}
          </p>
        )}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "var(--chat-space-1) 0 var(--chat-space-3)",
          }}
        >
          {searching ? (
            <SearchSection
              loading={loading}
              results={results}
              term={trimmed}
              isValidMxid={isValidMxid}
              mxidEntry={mxidEntry}
              busy={busy}
              onSelect={(person) => void handleSelect(person)}
            />
          ) : (
            <KnownPeopleSection
              people={knownPeople}
              busy={busy}
              onSelect={(person) => void handleSelect(person)}
            />
          )}
        </div>
      </div>
    </Dialog>
  );
}

function KnownPeopleSection({
  people,
  busy,
  onSelect,
}: {
  people: UserDirectoryEntry[];
  busy: boolean;
  onSelect: (person: UserDirectoryEntry) => void;
}) {
  if (people.length === 0) {
    return <StateRow>{mobileStrings.newConversation.noKnownPeople}</StateRow>;
  }
  return (
    <ul
      aria-label={mobileStrings.newConversation.knownPeopleLabel}
      style={{ listStyle: "none", margin: 0, padding: 0 }}
    >
      {people.map((person) => (
        <li key={person.userId}>
          <PersonRow person={person} busy={busy} onSelect={onSelect} />
        </li>
      ))}
    </ul>
  );
}

function SearchSection({
  loading,
  results,
  term,
  isValidMxid,
  mxidEntry,
  busy,
  onSelect,
}: {
  loading: boolean;
  results: UserDirectoryEntry[];
  term: string;
  isValidMxid: boolean;
  mxidEntry: UserDirectoryEntry;
  busy: boolean;
  onSelect: (person: UserDirectoryEntry) => void;
}) {
  if (loading) {
    return <StateRow>{mobileStrings.newConversation.searching}</StateRow>;
  }
  if (results.length > 0) {
    return (
      <ul
        aria-label={mobileStrings.newConversation.resultsLabel}
        style={{ listStyle: "none", margin: 0, padding: 0 }}
      >
        {results.map((person) => (
          <li key={person.userId}>
            <PersonRow person={person} busy={busy} onSelect={onSelect} />
          </li>
        ))}
      </ul>
    );
  }
  if (isValidMxid) {
    return (
      <ul
        aria-label={mobileStrings.newConversation.resultsLabel}
        style={{ listStyle: "none", margin: 0, padding: 0 }}
      >
        <li>
          <PersonRow
            person={mxidEntry}
            busy={busy}
            onSelect={onSelect}
            label={mobileStrings.newConversation.messageId(term)}
          />
        </li>
      </ul>
    );
  }
  if (term.startsWith("@")) {
    return <StateRow>{mobileStrings.newConversation.invalidId}</StateRow>;
  }
  return <StateRow>{mobileStrings.newConversation.noResults(term)}</StateRow>;
}

function PersonRow({
  person,
  busy,
  onSelect,
  label,
}: {
  person: UserDirectoryEntry;
  busy: boolean;
  onSelect: (person: UserDirectoryEntry) => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => onSelect(person)}
      aria-label={label ?? `${person.displayName} ${person.userId}`}
      className="chat-button outline-none focus-visible:shadow-neon-cyan"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-2)",
        width: "100%",
        minHeight: 52,
        padding: "0 var(--chat-space-4)",
        border: "none",
        borderRadius: 0,
        background: "transparent",
        textAlign: "left",
        cursor: busy ? "default" : "pointer",
      }}
    >
      <UserAvatar name={person.displayName} />
      <span
        data-feedback-redact="true"
        style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column" }}
      >
        <span
          style={{
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label ?? person.displayName}
        </span>
        {label === undefined && (
          <span
            style={{
              color: "var(--chat-fg-muted)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {person.userId}
          </span>
        )}
      </span>
    </button>
  );
}

function StateRow({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        padding: "var(--chat-space-3) var(--chat-space-4)",
        color: "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-sm-size)",
        lineHeight: "var(--chat-type-body-sm-line)",
      }}
    >
      {children}
    </p>
  );
}

/** Integration users (Hookshot and its webhook puppets) are not people to
 *  start a conversation with. */
function isIntegrationBot(userId: string): boolean {
  const localpart = /^@([^:]+):/.exec(userId)?.[1] ?? "";
  return localpart === "hookshot" || localpart.startsWith("_webhooks_");
}
